import React, {useMemo} from "react";

const ALLOWED_TAGS = new Set([
    "P", "SPAN", "STRONG", "B", "EM", "I", "U", "S",
    "H1", "H2", "H3", "H4", "H5", "H6",
    "UL", "OL", "LI", "BR", "BLOCKQUOTE", "HR",
    "A", "IMG", "DIV",
]);

const SAFE_URL_PREFIXES = ["https://", "http://", "mailto:", "tel:", "/"];

function isSafeUrl(url) {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim().toLowerCase();
    // Block any javascript: or data: URIs
    // eslint-disable-next-line no-script-url
    if (trimmed.startsWith("javascript:") || trimmed.startsWith("data:") || trimmed.startsWith("vbscript:")) {
        return false;
    }
    return SAFE_URL_PREFIXES.some(prefix => trimmed.startsWith(prefix));
}

/**
 * Sanitizes HTML using a strict allowlist with native DOMParser.
 * - Explicit element allowlist (blocks script, template, base, meta, svg, object, embed, iframe, etc).
 * - Explicit attribute allowlist with safe URL prefix verification.
 * - Fails closed if DOMParser is unavailable.
 */
export function sanitizeHtml(rawHtml) {
    if (!rawHtml || typeof rawHtml !== "string") return "";
    if (typeof window === "undefined" || !window.DOMParser) return ""; // Fail closed

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(rawHtml, "text/html");

        // Remove dangerous tags and template content explicitly
        const allElements = Array.from(doc.body.querySelectorAll("*"));

        allElements.forEach(el => {
            const tagName = el.tagName.toUpperCase();

            // Strip any tag not explicitly in the allowlist
            if (!ALLOWED_TAGS.has(tagName)) {
                el.remove();
                return;
            }

            // Sanitize attributes on allowed elements
            const attrs = Array.from(el.attributes);
            attrs.forEach(attr => {
                const attrName = attr.name.toLowerCase();
                const attrValue = attr.value;

                // Remove any inline event handler (on*)
                if (attrName.startsWith("on")) {
                    el.removeAttribute(attr.name);
                    return;
                }

                // Check link destinations
                if (attrName === "href" || attrName === "xlink:href" || attrName === "formaction") {
                    if (tagName === "A" && isSafeUrl(attrValue)) {
                        el.setAttribute("href", attrValue);
                        el.setAttribute("rel", "noopener noreferrer");
                    } else {
                        el.removeAttribute(attr.name);
                    }
                    return;
                }

                // Check image sources
                if (attrName === "src") {
                    if (tagName === "IMG" && isSafeUrl(attrValue)) {
                        el.setAttribute("src", attrValue);
                    } else {
                        el.removeAttribute(attr.name);
                    }
                    return;
                }

                // Allow safe layout/styling/link attributes
                const isGeneralSafeAttr = ["class", "alt", "title", "width", "height", "rel", "target"].includes(attrName);
                if (!isGeneralSafeAttr) {
                    el.removeAttribute(attr.name);
                }
            });
        });

        return doc.body.innerHTML;
    } catch {
        return "";
    }
}

export const SanitizedHtml = ({html, className, sx}) => {
    const cleanHtml = useMemo(() => sanitizeHtml(html), [html]);
    return (
        <div
            className={className}
            dangerouslySetInnerHTML={{__html: cleanHtml}}
        />
    );
};
